import React, { useState, useRef } from "react";
import "./ChatWidget.css";
import { FiMessageSquare } from "react-icons/fi";
import Logo from "../../../assets/Logo.png";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [userInput, setUserInput] = useState("");
  const [chatLog, setChatLog] = useState([]);
  const [userData, setUserData] = useState({
    name: "",
    company: "",
    email: "",
  });
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(true);
  const [welcomeVisible, setWelcomeVisible] = useState(true);
  const [timestamp, setTimestamp] = useState("");
  const [isHovering, setIsHovering] = useState(false);
  const initialTimestamp = useRef(null);

  const isMobile = window.innerWidth <= 768; // detect mobile

  const formatDateTime = (date) => {
    const day = date.getDate();
    const daySuffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";
    const month = date.toLocaleString("default", { month: "long" });
    const time = date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${month} ${day}${daySuffix} at ${time}`;
  };

  const toggleChat = () => {
    if (!isOpen) {
      if (!initialTimestamp.current) {
        const now = new Date();
        const formatted = formatDateTime(now);
        initialTimestamp.current = formatted;
        setTimestamp(formatted);
      }
      setWelcomeVisible(false);
    } else {
      setWelcomeVisible(true);
    }
    setIsOpen(!isOpen);
  };

  const resetConversation = () => {
    setStep(0);
    setUserInput("");
    setChatLog([]);
    setUserData({ name: "", company: "", email: "" });
    setIsTyping(false);
    setShowOptions(true);
    const now = new Date();
    setTimestamp(formatDateTime(now));
  };

  const handleOptionClick = (option) => {
    setShowOptions(false);
    setIsTyping(true);
    let newChatLog = [...chatLog];

    setTimeout(() => {
      if (option === "browse") {
        newChatLog.push({ sender: "user", text: "I’m just browsing" });
        setChatLog(newChatLog);
        setTimeout(() => {
          newChatLog.push({
            sender: "system",
            text: "Cool, I’m here if you need any help!",
          });
          setChatLog([...newChatLog]);
          setIsTyping(false);
          setStep(5);
        }, 1000);
      } else if (option === "help") {
        newChatLog.push({ sender: "user", text: "I’d like some help" });
        setChatLog(newChatLog);
        setTimeout(() => {
          newChatLog.push({
            sender: "system",
            text: `Thanks for reaching out. Please email us at <strong>bd@varanouslabs.com</strong> or chat with us on <strong>+91 9063488659</strong> on WhatsApp.`,
          });
          setChatLog([...newChatLog]);
          setIsTyping(false);
          setStep(5);
        }, 1000);
      } else {
        newChatLog.push({ sender: "user", text: "I’m looking to buy" });
        setChatLog(newChatLog);
        setTimeout(() => {
          newChatLog.push({ sender: "system", text: "That’s great to hear!" });
          newChatLog.push({
            sender: "system",
            text: "Could I have your name?",
          });
          setChatLog([...newChatLog]);
          setIsTyping(false);
          setStep(1);
        }, 1000);
      }
    }, 500);
  };

  const handleSend = () => {
    const input = userInput.trim();
    if (!input) return;
    let newChatLog = [...chatLog, { sender: "user", text: input }];
    setChatLog(newChatLog);
    setIsTyping(true);

    if (step === 1) {
      setTimeout(() => {
        newChatLog.push({ sender: "system", text: "Thank you!" });
        newChatLog.push({
          sender: "system",
          text: "What’s your company’s name?",
        });
        setChatLog([...newChatLog]);
        setIsTyping(false);
        setUserData({ ...userData, name: input });
        setStep(2);
      }, 1000);
    } else if (step === 2) {
      setTimeout(() => {
        newChatLog.push({
          sender: "system",
          text: "Can I please have your email so a member of our sales team can follow up?",
        });
        setChatLog([...newChatLog]);
        setIsTyping(false);
        setUserData({ ...userData, company: input });
        setStep(3);
      }, 1000);
    } else if (step === 3) {
      setTimeout(() => {
        newChatLog.push({
          sender: "system",
          text: `Thank you. We’ll be in touch. Contact: <strong>bd@varanouslabs.com</strong> or <strong>+91 9063488659</strong> on WhatsApp.`,
        });
        setChatLog([...newChatLog]);
        setIsTyping(false);
        setUserData({ ...userData, email: input });
        setStep(4);
      }, 1000);
    }
    setUserInput("");
  };

  return (
    <div className="chat-container">
      {!isOpen && (
        <div className="chat-minimized" onClick={toggleChat}>
          {/* Only show welcome bubble on desktop */}
          {welcomeVisible && !isMobile && (
            <div
              className="chat-welcome-bubble"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {isHovering && (
                <button
                  className="chat-welcome-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setWelcomeVisible(false);
                  }}
                >
                  ×
                </button>
              )}
              <img
                src={Logo}
                alt="icon"
                width="30"
                style={{ marginRight: "12px", verticalAlign: "middle" }}
              />
              <p>Welcome to Varanous Labs</p>
            </div>
          )}

          <FiMessageSquare className="chat-icon" />
        </div>
      )}

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <span className="chat-title" onClick={resetConversation}>
              Varanous Labs
            </span>
            <button className="chat-close-button" onClick={toggleChat}>
              ×
            </button>
          </div>

          <div className="chat-body">
            {timestamp && (
              <p className="timestamp">
                <strong>{timestamp}</strong>
              </p>
            )}

            {step === 0 && (
              <>
                <div className="chat-message system">
                  <p>Welcome to Varanous Labs</p>
                  <p>What brought you to our website today?</p>
                </div>
                {showOptions && (
                  <div className="chat-options">
                    <button onClick={() => handleOptionClick("buy")}>
                      I’m looking to buy
                    </button>
                    <button onClick={() => handleOptionClick("browse")}>
                      I’m just browsing
                    </button>
                    <button onClick={() => handleOptionClick("help")}>
                      I’d like some help
                    </button>
                  </div>
                )}
              </>
            )}

            {chatLog.map((msg, index) => (
              <div
                key={index}
                className={`chat-message ${msg.sender}`}
                dangerouslySetInnerHTML={{ __html: msg.text }}
              />
            ))}

            {isTyping && (
              <div className="chat-message system">
                <span>...</span>
              </div>
            )}

            {step > 0 && step < 4 && (
              <div className="chat-input-area">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Type your response..."
                />
                <button onClick={handleSend}>➤</button>
              </div>
            )}

            {(step === 4 || step === 5) && (
              <div className="chat-restart">
                <button onClick={resetConversation}>
                  🔄 Restart conversation
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
